"use client";

import {
  FC,
  useCallback,
  useEffect,
  useState,
} from "react";
import Icon from "apps/website/components/base/Icon/Icon";
import TextControl from "apps/website/components/base/Text/TextControl/TextControl";
import TextSubtitle from "apps/website/components/base/Text/TextSubtitle/TextSubtitle";
import { useAnalytics } from "apps/website/hooks/useAnalytics";
import cx from "apps/website/utils/misc/cx";

export interface IArticleShare {
  title: string;
  url: string;
}

const SHARE_EVENT = "article_shared";
const COPIED_RESET_MS = 2000;

const canUseNativeShare = (): boolean =>
  typeof navigator !== "undefined" && typeof navigator.share === "function";

const ArticleShare: FC<IArticleShare> = ({ title, url }) => {
  const analytics = useAnalytics();
  const [ nativeShareEnabled, setNativeShareEnabled ] = useState(false);
  const [ copied, setCopied ] = useState(false);

  // navigator.share is only known client-side; resolve after mount to avoid hydration mismatch.
  useEffect(() => {
    setNativeShareEnabled(canUseNativeShare());
  }, []);

  const trackShare = useCallback((channel: string) => {
    void analytics?.track(SHARE_EVENT, {
      channel,
      article_title: title,
      article_url: url,
    });
  }, [ analytics, title, url ]);

  const nativeShare = useCallback(async () => {
    try {
      await navigator.share({ title, url });
      trackShare("native");
    } catch {
      // The reader dismissed the native share sheet, or sharing was cancelled. Nothing to do.
    }
  }, [ title, url, trackShare ]);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      trackShare("copy_link");
      setTimeout(() => setCopied(false), COPIED_RESET_MS);
    } catch {
      // Clipboard access was denied or unavailable. Nothing else we can do here.
    }
  }, [ url, trackShare ]);

  const handleShare = useCallback(() => {
    if (nativeShareEnabled) {
      void nativeShare();
      return;
    }
    void copyLink();
  }, [ nativeShareEnabled, nativeShare, copyLink ]);

  const label = copied ? "Link copied!" : "Share the love";

  return (
    <div data-component="ArticleShare" className="flex flex-col items-center gap-4">
      <TextSubtitle tag="h3" size={1} align="center">Liked this article?</TextSubtitle>
      <button
        type="button"
        onClick={handleShare}
        data-testid="article-share-button"
        aria-label="Share this article"
        className={cx(
          "flex w-full items-center justify-center gap-2 rounded-full bg-black px-4 py-2",
          "shadow-md transition-opacity duration-300 lg:hover:opacity-70",
        )}
      >
        <TextControl tag="span" size={2} align="center" className="text-white">{ label }</TextControl>
        <Icon icon="send" color="inherit" size="2xsmall" className="text-white" ariaHidden />
      </button>
    </div>
  );
};

export default ArticleShare;
